import { Button } from "@astryxdesign/core/Button";
import { DateInput } from "@astryxdesign/core/DateInput";
import { Dialog, DialogHeader } from "@astryxdesign/core/Dialog";
import { Icon } from "@astryxdesign/core/Icon";
import { IconButton } from "@astryxdesign/core/IconButton";
import { Layout, LayoutContent, LayoutFooter } from "@astryxdesign/core/Layout";
import { ProgressBar } from "@astryxdesign/core/ProgressBar";
import {
  SegmentedControl,
  SegmentedControlItem,
} from "@astryxdesign/core/SegmentedControl";
import { HStack, StackItem, VStack } from "@astryxdesign/core/Stack";
import { Text } from "@astryxdesign/core/Text";
import { TextArea } from "@astryxdesign/core/TextArea";
import { TextInput } from "@astryxdesign/core/TextInput";
import { Token } from "@astryxdesign/core/Token";
import { useToast } from "@astryxdesign/core/Toast";
import { useRef, useState } from "react";
import type { Currency, MoneyState, SubPatch, Subscription } from "../../../shared/money";
import { submitOnEnter } from "../../lib/forms";
import type { IsoDate } from "../../lib/format";
import { CheckIcon, ExternalIcon, TrashIcon } from "../../lib/icons";
import {
  dueColor,
  dueLabel,
  formatDay,
  formatMoney,
  formatUah,
  installmentProgress,
  toMajor,
  toMinor,
} from "../../lib/money";
import { useDeleteSub, usePatchSub, usePayCharge } from "../../lib/money-queries";
import { occurrenceList } from "../../lib/schedule";

interface Props {
  sub: Subscription;
  state: MoneyState;
  onClose: () => void;
}

/** Mounted with key={sub.id}: local text state resets per subscription. */
export function SubDialog({ sub, state, onClose }: Props) {
  const toast = useToast();
  const patchSub = usePatchSub();
  const deleteSub = useDeleteSub();
  const pay = usePayCharge();

  const [name, setName] = useState(sub.name);
  const [amount, setAmount] = useState(String(toMajor(sub.amount)));
  const [card, setCard] = useState(sub.card ?? "");
  const [notes, setNotes] = useState(sub.notes);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const confirmTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const patch = (p: SubPatch) => patchSub.mutate({ id: sub.id, patch: p });

  function commitText() {
    const trimmed = name.trim();
    if (trimmed && trimmed !== sub.name) patch({ name: trimmed });
    const minor = toMinor(Number(amount.replace(",", ".")));
    if (Number.isFinite(minor) && minor > 0 && minor !== sub.amount) patch({ amount: minor });
    const cleanCard = card.trim() || null;
    if (cleanCard !== sub.card) patch({ card: cleanCard });
    if (notes !== sub.notes) patch({ notes });
  }

  const close = () => {
    commitText();
    onClose();
  };

  const payments = state.payments.filter((p) => p.subId === sub.id);
  const settled = new Set(payments.map((p) => p.dueDate));
  const progress = sub.kind === "installment" ? installmentProgress(sub, payments) : null;
  const schedule = occurrenceList(sub, sub.kind === "installment" ? (sub.parts ?? 0) : 6);
  const nextUnsettled = schedule.find((d) => !settled.has(d));

  function handleDelete() {
    if (!confirmDelete) {
      setConfirmDelete(true);
      clearTimeout(confirmTimer.current);
      confirmTimer.current = setTimeout(() => setConfirmDelete(false), 2500);
      return;
    }
    deleteSub.mutate(sub.id);
    onClose();
    toast({ body: `Deleted “${sub.name}”`, uniqueID: "sub-deleted" });
  }

  return (
    <Dialog
      isOpen
      onOpenChange={(open) => !open && close()}
      width={560}
      maxHeight="calc(100dvh - 2 * max(env(safe-area-inset-top, 0px), env(safe-area-inset-bottom, 0px)) - 32px)"
      purpose="form"
    >
      <Layout
        header={
          <DialogHeader
            title={sub.kind === "installment" ? "Installment" : "Subscription"}
            onOpenChange={(open) => !open && close()}
            endContent={
              sub.url ? (
                <IconButton
                  label="Open service page"
                  tooltip="Open service page"
                  size="sm"
                  variant="ghost"
                  icon={<ExternalIcon />}
                  onClick={() => window.open(sub.url ?? "", "_blank", "noopener,noreferrer")}
                />
              ) : undefined
            }
          />
        }
        content={
          <LayoutContent>
            <VStack gap={4}>
              <TextInput
                label="Name"
                isLabelHidden
                size="lg"
                value={name}
                onChange={setName}
                onBlur={commitText}
                placeholder="Service name"
              />

              <HStack gap={2} wrap="wrap" align="end">
                <StackItem size="fill">
                  <TextInput
                    label={sub.kind === "installment" ? "Per part" : "Amount"}
                    size="sm"
                    value={amount}
                    onChange={setAmount}
                    onBlur={commitText}
                  />
                </StackItem>
                <SegmentedControl
                  label="Currency"
                  size="sm"
                  value={sub.currency}
                  onChange={(v) => patch({ currency: v as Currency })}
                  isDisabled={sub.kind === "installment"}
                >
                  <SegmentedControlItem value="UAH" label="₴" />
                  <SegmentedControlItem value="USD" label="$" />
                  <SegmentedControlItem value="EUR" label="€" />
                </SegmentedControl>
              </HStack>

              <HStack gap={2} wrap="wrap" align="end">
                {sub.kind === "recurring" ? (
                  <SegmentedControl
                    label="Cycle"
                    size="sm"
                    value={sub.cycle ?? "monthly"}
                    onChange={(v) => patch({ cycle: v as "monthly" | "yearly" })}
                  >
                    <SegmentedControlItem value="monthly" label="Monthly" />
                    <SegmentedControlItem value="yearly" label="Yearly" />
                  </SegmentedControl>
                ) : (
                  <Text type="supporting" size="xsm">
                    {sub.parts} parts · total {formatMoney((sub.parts ?? 0) * sub.amount, sub.currency)}
                  </Text>
                )}
                <StackItem size="fill">
                  <DateInput
                    label="First charge"
                    size="sm"
                    format="date"
                    value={sub.firstDate as IsoDate}
                    onChange={(v) => v && patch({ firstDate: v })}
                  />
                </StackItem>
              </HStack>

              <TextInput
                label="Card"
                size="sm"
                placeholder="Which card pays for it"
                value={card}
                onChange={setCard}
                onBlur={commitText}
              />

              {progress ? (
                <VStack gap={1.5}>
                  <HStack gap={2} align="center">
                    <StackItem size="fill">
                      <Text type="label" color="secondary">
                        {progress.total - progress.paid} payments left ·{" "}
                        {formatUah(progress.leftUah)}
                      </Text>
                    </StackItem>
                    <Text type="supporting" size="xsm">
                      paid {formatUah(progress.paidUah)}
                    </Text>
                  </HStack>
                  <ProgressBar
                    value={Math.round(progress.ratio * 100)}
                    label="Installment progress"
                    isLabelHidden
                  />
                </VStack>
              ) : null}

              <VStack gap={1.5}>
                <Text type="label" color="secondary">
                  {sub.kind === "installment" ? "Schedule" : "Upcoming"}
                </Text>
                {schedule.map((day, i) => {
                  const isPaid = settled.has(day);
                  const isNext = day === nextUnsettled;
                  const daysLeft = state.due.find(
                    (d) => d.subId === sub.id && d.dueDate === day,
                  )?.daysLeft;
                  return (
                    <HStack key={day} gap={2} align="center">
                      <Icon
                        icon={CheckIcon}
                        size="sm"
                        color={isPaid ? "accent" : "disabled"}
                        label={isPaid ? "Paid" : "Not paid"}
                      />
                      <StackItem size="fill">
                        <HStack gap={1.5} align="center">
                          <Text
                            type="body"
                            color={isPaid ? "secondary" : "primary"}
                            hasStrikethrough={isPaid}
                          >
                            {formatDay(day)}
                          </Text>
                          {sub.kind === "installment" ? (
                            <Text type="supporting" size="xsm">
                              {i + 1}/{sub.parts}
                            </Text>
                          ) : null}
                          {isNext && daysLeft !== undefined ? (
                            <Token size="sm" color={dueColor(daysLeft)} label={dueLabel(daysLeft)} />
                          ) : null}
                        </HStack>
                      </StackItem>
                      <Text type="supporting" size="xsm">
                        {formatMoney(sub.amount, sub.currency)}
                      </Text>
                      <Button
                        label={isPaid ? "Undo" : "Pay"}
                        size="sm"
                        variant={isNext ? "primary" : "ghost"}
                        isLoading={pay.isPending}
                        onClick={() =>
                          pay.mutate({ id: sub.id, dueDate: day, undo: isPaid })
                        }
                      />
                    </HStack>
                  );
                })}
              </VStack>

              <TextArea
                label="Notes"
                isLabelHidden
                placeholder="Notes"
                rows={2}
                value={notes}
                onChange={setNotes}
                onBlur={commitText}
                onKeyDown={submitOnEnter(commitText)}
              />
            </VStack>
          </LayoutContent>
        }
        footer={
          <LayoutFooter>
            <HStack gap={2} align="center">
              {sub.kind === "recurring" ? (
                <Button
                  label={sub.paused ? "Resume" : "Pause"}
                  variant="secondary"
                  onClick={() => patch({ paused: !sub.paused })}
                />
              ) : null}
              <StackItem size="fill">
                <Text type="supporting" size="xsm" color="secondary">
                  next {nextUnsettled ? formatDay(nextUnsettled) : "—"}
                </Text>
              </StackItem>
              <Button
                label={confirmDelete ? "Really delete?" : "Delete"}
                variant="destructive"
                icon={<TrashIcon />}
                onClick={handleDelete}
              />
            </HStack>
          </LayoutFooter>
        }
      />
    </Dialog>
  );
}
